import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { environment } from '@environments/environment';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { RegistroCivilService } from '@services/registro-civil.service';
import { InputNumberModule } from 'primeng/inputnumber';
import { IftaLabelModule } from 'primeng/iftalabel';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DatePickerModule } from 'primeng/datepicker';
import { UserService } from '@services/user.service';
import { DrawerModule } from 'primeng/drawer';
import { PlaceholderTable } from '@components/placeholders/table/table.component';
import { ExcelService } from '@services/excel.service';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TooltipModule } from 'primeng/tooltip';
import { ToastService } from '@utils/toast.service';
import { ShowForRolesDirective } from '@directives/show-for-roles.directive';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'page-buscar-acto',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    RouterModule,
    InputMaskModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    InputMaskModule,
    InputNumberModule,
    IftaLabelModule,
    TagModule,
    ToggleSwitchModule,
    DatePickerModule,
    DrawerModule,
    PlaceholderTable,
    InputGroupAddonModule,
    InputGroup,
    TooltipModule,
    ShowForRolesDirective,
    DialogModule,
  ],
  templateUrl: './buscar-acto.component.html',
  styleUrl: './buscar-acto.component.scss',
})
export class BuscarActoPage {
  private formBuilder = inject(FormBuilder);
  private registroCivilService = inject(RegistroCivilService);
  private userService = inject(UserService);
  private excelService = inject(ExcelService);
  private toastService = inject(ToastService);

  public apiUrl = environment.app.apiUrl;
  public isLoading: boolean = false;
  public isSaving: boolean = false;

  public list: any = [];
  public searchValue: string | undefined;
  public searchByList: { label: string; value: string }[] = [
    {
      label: 'CURP',
      value: '1',
    },
    {
      label: 'Cadena',
      value: '2',
    },
  ];
  public typeActoList: { label: string; value: string }[] = [
    {
      label: 'Nacimiento',
      value: '1',
    },
    {
      label: 'Defunción',
      value: '2',
    },
    {
      label: 'Matrimonio',
      value: '3',
    },
    {
      label: 'Divorcio',
      value: '4',
    },
  ];
  public statusList: any = [
    {
      label: 'Completo',
      value: 'complete',
    },
    {
      label: 'Procesando',
      value: 'processing',
    },
    {
      label: 'Error',
      value: 'error',
    },
  ];
  public modalFilterVisible: boolean = false;
  public modalVisibleError: boolean = false;
  public modalErrorMsg: string = '';

  public filter: {
    dateFrom: Date | null;
    dateTo: Date | null;
    status: string | null;
  } = {
    dateFrom: null,
    dateTo: null,
    status: null,
  };

  public form = this.formBuilder.group({
    platform: ['SIDEA', Validators.required],
    searchBy: ['1', Validators.required],
    typeActo: ['1'],
    curp: [null],
    cadena: [null],
    folioReverso: ['reverso'],
  });

  get platform() {
    return this.form.get('platform');
  }
  set platform(value: any) {
    this.platform.setValue(value);
  }
  get searchBy() {
    return this.form.get('searchBy');
  }
  set searchBy(value: any) {
    this.searchBy.setValue(value);
  }
  get typeActo() {
    return this.form.get('typeActo');
  }
  set typeActo(value: any) {
    this.typeActo.setValue(value);
  }
  get curp() {
    return this.form.get('curp');
  }
  set curp(value: any) {
    this.curp.setValue(value);
  }
  get cadena() {
    return this.form.get('cadena');
  }
  set cadena(value: any) {
    this.cadena.setValue(value);
  }
  get folioReverso() {
    return this.form.get('folioReverso');
  }
  set folioReverso(value: any) {
    this.folioReverso.setValue(value);
  }

  async ngOnInit() {
    this.searchBy.valueChanges.subscribe((value: any) => {
      this.typeActo.setValidators(null);
      this.curp.setValidators(null);
      this.cadena.setValidators(null);

      this.typeActo = null;
      this.curp = null;
      this.cadena = null;
      this.folioReverso = 'reverso';

      if (value === '1') {
        this.typeActo.setValidators([Validators.required]);
        this.curp.setValidators([Validators.required, Validators.minLength(18), Validators.maxLength(18)]);
      }

      if (value === '2') {
        this.cadena.setValidators([Validators.required, Validators.minLength(20), Validators.maxLength(20)]);
      }

      this.typeActo.updateValueAndValidity();
      this.curp.updateValueAndValidity();
      this.cadena.updateValueAndValidity();
    });

    await this.refresh();
  }

  refresh = async () => {
    this.registroCivilService.listSearchActos().then((response) => (this.list = response));
  };

  search = async () => {
    this.isSaving = true;

    let body = {
      platform: this.platform.value,
      searchBy: this.searchBy.value,
      folioReverso: this.folioReverso.value,
    };

    if (this.searchBy.value === '1') {
      Object.assign(body, {
        typeActo: this.typeActo.value,
        curp: this.curp.value,
      });
    }
    if (this.searchBy.value === '2') {
      Object.assign(body, {
        cadena: this.cadena.value,
      });
    }

    this.form.reset();
    this.platform = 'SIDEA';
    this.folioReverso = 'reverso';

    this.isSaving = false;

    this.refresh();

    this.registroCivilService
      .searchSidea(body)
      .then(() => this.refresh())
      .catch((err) => {
        this.toastService.error(err.error.data.message || 'Error al buscar el acto.');
      });
  };

  filterApply = async (clear = false) => {
    this.isLoading = true;

    if (clear === true) {
      this.filter.dateFrom = null;
      this.filter.dateTo = null;
      this.filter.status = null;
    }

    let queryParams = new URLSearchParams();

    if (this.filter.dateFrom) {
      queryParams.append('dateFrom', (new Date(this.filter.dateFrom).getTime() / 1000).toFixed());
    }
    if (this.filter.dateTo) {
      queryParams.append('dateTo', (new Date(this.filter.dateTo).getTime() / 1000).toFixed());
    }
    if (this.filter.status) {
      queryParams.append('status', this.filter.status);
    }

    this.registroCivilService.listSearchActos(queryParams.toString()).then((response) => {
      this.list = response;
      this.isLoading = false;
    });

    this.modalFilterVisible = false;
  };

  exportToExcel = (): void => {
    const report: any = [];

    this.list.forEach((item: any) => {
      if (item.error !== null) {
        item.status = item.error;
      } else {
        if (item.file === null) {
          item.status = 'Procesando..';
        } else {
          item.status = 'Completo';
        }
      }

      report.push({
        // ID: String(item.id),
        FECHA: item.dateCreated,
        ENTIDAD: item.entidad,
        DATO: item.typeSearch === 1 ? item.curp : item.cadena,
        TIPO: item.typeActo !== null ? String(item.typeActo).toUpperCase() : '',
        // ESTATUS: item.status,
        // PLATAFORMA: item.platform,
        // ROBOT: item.robotLabel,
      });
    });

    var today = new Date();
    var dd = String(today.getDate()).padStart(2, '0');
    var mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    var yyyy = today.getFullYear();
    var hh = String(today.getHours()).padStart(2, '0');
    var ii = String(today.getMinutes()).padStart(2, '0');
    var ss = String(today.getSeconds()).padStart(2, '0');

    this.excelService.generateExcel(report, `Reporte, Registro Civil, Buscar Acto - ${mm}-${dd}-${yyyy} ${hh}.${ii}.${ss}`);
  };

  showError = (error: string) => {
    this.modalVisibleError = true;
    this.modalErrorMsg = error;
  };
}
